import React, {
  createContext,
  useContext,
  //useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
//import useFetchAssetData from "../Hooks/fetchAssetsHook";
//import { env } from "../../../env";
import useLocalStorage from "../Hooks/useLocalStorage";
import { AppContext } from "../../../App";

//const baseURL = env?.microservice_assetsmgt;

// Define the data structure
interface InjectionDataItem {
  id: string;
  plantId: string;
  usecaseId: string;
  levelOneId: string;
}

// Define the context type
interface InjectionDataContextType {
  sharedData: InjectionDataItem[];
  setSharedData: React.Dispatch<React.SetStateAction<InjectionDataItem[]>>;
  updateField: (
    id: string,
    key: keyof InjectionDataItem,
    newValue: any
  ) => void;
}

// Create Context with undefined default
const InjectionDataContext = createContext<
  InjectionDataContextType | undefined
>(undefined);

// Provider Component
export const InjectionDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const appContextProps: any = useContext(AppContext);
  const [sharedData, setSharedData] = useLocalStorage<InjectionDataItem[]>(
    "injection-pdm-shared-data",
    []
  );
  // const [sharedData, setSharedData] = useState<InjectionDataItem[]>([]);

  // Fetch Redux state
  const usecaseList = useSelector((state: any) => state.usecaseList);
  const plantsList = useSelector((state: any) => state.plantsList);
  const levelsList = useSelector((state: any) => state.assetsList);


  // // Fetch external API data
  // const {
  //   data: levelOneData,
  //   isSuccess,
  //   isFetching,
  // } = useFetchAssetData(
  //   `${baseURL}leveltypes`,
  //   "level_one_id",
  //   `filter=level_types_code:eq:L1`
  // );

  // Extract usecaseId from Redux state
  const selectedUsecaseId = useMemo(() => {
    return (
      usecaseList?.usecases?.find(
        (u: any) => u.routes_use_code === "injection-pdm"
      )?._id ?? ""
    );
  }, [usecaseList]);

  // Extract plantId from Redux state
  const plantCode = localStorage.getItem("InjectionPdmDefaultPlantCode")
  const validPlantCode = plantCode ? plantCode : appContextProps.selectedPlantForCube
  appContextProps.setSelectedPlantForCube(validPlantCode)
  const selectedPlantId = useMemo(() => {
    return (
      plantsList?.plants?.find(
        (p: any) => p.plant_code === validPlantCode
      )?._id ?? ""
    );
  }, [appContextProps.selectedPlantForCube, plantsList, validPlantCode]);
  
  // Extract LevelId from Redux state
  const selectedLevelId = useMemo(() => {
    return (
      levelsList?.assets?.find((p: any) => p.level_types_code === "L1")?._id ??
      ""
    );
  }, [levelsList]);

  // Update sharedData when computedSharedData changes
  // **🚀 Preserve updates in state instead of overwriting**
  useEffect(() => {
    if (selectedPlantId && selectedUsecaseId && selectedLevelId) {
      setSharedData((prevData) => {
        const newData = [
          {
            id: "InjectionData",
            plantId: selectedPlantId,
            usecaseId: selectedUsecaseId,
            levelOneId: selectedLevelId,
          },
        ];
        return JSON.stringify(prevData) === JSON.stringify(newData)
          ? prevData
          : newData;
      });
    }
  }, [selectedLevelId, selectedPlantId, selectedUsecaseId, setSharedData]);

  // Update a specific field in sharedData
  const updateField = (
    id: string,
    key: keyof InjectionDataItem,
    newValue: any
  ) => {
    setSharedData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, [key]: newValue } : item
      )
    );
  };
  return (
    <InjectionDataContext.Provider
      value={{ sharedData, setSharedData, updateField }}
    >
      {children}
    </InjectionDataContext.Provider>
  );
};

// Custom Hook to Access Context
export const useInjectionDataContext = (): InjectionDataContextType => {
  const context = useContext(InjectionDataContext);
  if (!context) {
    throw new Error(
      "useInjectionDataContext must be used within an InjectionDataProvider"
    );
  }
  return context;
};
